import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  listManagePlansApi,
  listFeaturesApi,
  createPlanApi,
  updatePlanApi,
  setPlanActiveApi,
  type CreatePlanBody,
  type UpdatePlanBody,
} from "@/lib/api";

// Shared with the read-only usePlans() dropdown hook (useClients.ts) so both
// invalidate the same cache — the manager list is a superset (incl. disabled).
const PLANS_KEY = ["super-admin", "plans"] as const;

export function useManagePlans() {
  return useQuery({
    queryKey: [...PLANS_KEY, "manage"],
    queryFn: listManagePlansApi,
  });
}

export function useFeatures() {
  return useQuery({
    queryKey: ["super-admin", "features"],
    queryFn: listFeaturesApi,
    staleTime: 5 * 60_000,
  });
}

function useInvalidatePlans() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: PLANS_KEY });
}

export function useCreatePlan() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (input: CreatePlanBody) => createPlanApi(input),
    onSuccess: () => {
      void invalidate();
      toast.success("Plan creado");
    },
    onError: (err: Error) => toast.error(err.message || "No se pudo crear el plan"),
  });
}

export function useUpdatePlan() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdatePlanBody & { id: string }) => updatePlanApi(id, input),
    onSuccess: () => {
      void invalidate();
      toast.success("Plan actualizado");
    },
    onError: (err: Error) => toast.error(err.message || "No se pudo actualizar el plan"),
  });
}

export function useSetPlanActive() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      setPlanActiveApi(id, isActive),
    onSuccess: (_data, { isActive }) => {
      void invalidate();
      toast.success(isActive ? "Plan habilitado" : "Plan deshabilitado");
    },
    onError: (err: Error) => toast.error(err.message || "No se pudo cambiar el estado del plan"),
  });
}
